import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Title, Text, Progress, ThemeIcon, Stack, Group } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Robot, CheckCircle, LinkSimple } from '@phosphor-icons/react';
import api from '../utils/api';

export default function Onboarding({ onComplete }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width: 48em)');

  const totalSteps = 3;

  const handleConnectGoogle = async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounts/google/auth/');
      window.location.href = res.data.auth_url;
    } catch {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    localStorage.setItem('droid_onboarding_complete', 'true');
    onComplete();
  };

  const btnSize = isMobile ? 'md' : 'sm';

  return (
    <Modal
      opened={true}
      withCloseButton={false}
      size="lg"
      centered={!isMobile}
      fullScreen={isMobile}
      radius={isMobile ? 0 : 'md'}
      padding={isMobile ? 'lg' : 'xl'}
      styles={isMobile ? {
        body: { minHeight: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
      } : undefined}
    >
      {/* Progress */}
      <Group justify="space-between" mb={4}>
        <Text size="xs" c="dimmed">{step}/{totalSteps}</Text>
      </Group>
      <Progress value={(step / totalSteps) * 100} color="brand" size="xs" radius="xl" mb="xl" />

      {/* Step 1: Welcome */}
      {step === 1 && (
        <Stack align="center" gap="md">
          <ThemeIcon size={56} radius="xl" color="brand" variant="light">
            <Robot size={28} weight="duotone" />
          </ThemeIcon>
          <Title order={3} ta="center">{t('shell.onboarding.step1.title')}</Title>
          <Text size="sm" c="dimmed" ta="center" lh={1.6}>
            {t('shell.onboarding.step1.text')}
          </Text>
          <Button color="brand" fullWidth={isMobile} size={btnSize} mt="sm" onClick={() => setStep(2)}>
            {t('shell.onboarding.step1.cta')}
          </Button>
        </Stack>
      )}

      {/* Step 2: Connect Google Ads */}
      {step === 2 && (
        <Stack align="center" gap="md">
          <ThemeIcon size={56} radius="xl" color="brand" variant="light">
            <LinkSimple size={28} weight="duotone" />
          </ThemeIcon>
          <Title order={3} ta="center">{t('shell.onboarding.step2.title')}</Title>
          <Text size="sm" c="dimmed" ta="center" lh={1.6}>
            {t('shell.onboarding.step2.text')}
          </Text>
          <Button color="brand" fullWidth size={btnSize} mt="sm" onClick={handleConnectGoogle} loading={loading}>
            {loading ? t('shell.onboarding.step2.connecting') : t('shell.onboarding.step2.cta')}
          </Button>
          <Button variant="subtle" color="gray" size="xs" onClick={() => setStep(3)}>
            {t('shell.onboarding.step2.skip')}
          </Button>
        </Stack>
      )}

      {/* Step 3: Done */}
      {step === 3 && (
        <Stack align="center" gap="md">
          <ThemeIcon size={56} radius="xl" color="green" variant="light">
            <CheckCircle size={28} />
          </ThemeIcon>
          <Title order={3} ta="center">{t('shell.onboarding.step3.title')}</Title>
          <Text size="sm" c="dimmed" ta="center" lh={1.6}>
            {t('shell.onboarding.step3.text')}
          </Text>
          <Button color="brand" fullWidth size={btnSize} mt="sm" onClick={handleFinish}>
            {t('shell.onboarding.step3.cta')}
          </Button>
        </Stack>
      )}
    </Modal>
  );
}
